import { forwardRef, Ref } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from './spinner.theme';

interface SpinnerProps {
	/**
	 * The visual size of the spinner
	 */
	size: 'xSmall' | 'small' | 'medium' | 'large';

	/**
	 * Reflects the value of the native HTML Element `class` property.
	 */
	className?: string;
}

const dash = keyframes`
	0% {
		stroke-dasharray: 1, 786;
		stroke-dashoffset: 0;
	}

	50% {
		stroke-dasharray: 700, 786;
		stroke-dashoffset: -0;
	}

	100% {
		stroke-dasharray: 700, 786;
		stroke-dashoffset: -736;
	}
`;

const rotate = keyframes`
	100% {
		transform: rotate(360deg);
	}
`;

const elements = {
	loader: styled.div`
		--size: 0;
		box-sizing: border-box;
		width: var(--size);
		height: var(--size);
		padding: 2px;

		svg {
			animation: ${rotate} 2s linear infinite;
		}

		svg circle {
			stroke-linecap: round;
			animation: ${dash} 3.5s ease-in-out infinite;
		}

		&[data-size='xSmall'] {
			--size: ${theme.getValue('xSmallSize')};
		}

		&[data-size='small'] {
			--size: ${theme.getValue('smallSize')};
		}

		&[data-size='medium'] {
			--size: ${theme.getValue('mediumSize')};
		}

		&[data-size='large'] {
			--size: ${theme.getValue('largeSize')};
		}
	`,
	svg: styled.svg`
		display: block;
		width: 100%;
		height: 100%;
		font-size: inherit;
		fill: currentColor;
		stroke: currentColor;
		pointer-events: none;
	`,
};

function CellulaSpinner({ size, ...rest }: SpinnerProps, ref: Ref<HTMLDivElement>): JSX.Element {
	return (
		<elements.loader data-testid='loading-spinner' data-size={size || undefined} {...rest} ref={ref}>
			<elements.svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256'>
				<circle cx='128' cy='128' r='118' fill='none' strokeWidth='7%'></circle>
			</elements.svg>
		</elements.loader>
	);
}

/* ########################################################################## */
/* ############################### PUBLIC API ############################### */
/* ########################################################################## */

/**
 * A component to display a spinner to indicate something is loading.
 */
const Spinner = forwardRef(CellulaSpinner);

export type { SpinnerProps };
export { Spinner };
